/**
 * LegalLayout — shared chrome for the customer-facing legal documents
 * (Privacy Policy, Terms & Conditions).
 *
 * Ported from `screens/src/pages/legal/LegalLayout.jsx`. Renders a sticky
 * top bar (logo + Print + Home + sibling pointer), a hero with title /
 * subtitle / effective+lastUpdated dates, a sticky table of contents with
 * scroll-spy, and a `legal-prose`-styled article body that accepts the
 * document content as `children`.
 *
 * Public — these pages are NOT auth-gated and are not routed through the
 * customer portal layout. They mount directly under `/legal/*` in App.tsx.
 */
import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Mail, Phone, Printer } from 'lucide-react';

export interface LegalSection {
  readonly id: string;
  readonly label: string;
}

export interface LegalSibling {
  readonly to: string;
  readonly label: string;
}

export interface LegalLayoutProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly lastUpdated?: string;
  readonly effective?: string;
  readonly sections?: readonly LegalSection[];
  readonly sibling?: LegalSibling;
  readonly children: ReactNode;
}

export function LegalLayout({
  title,
  subtitle,
  lastUpdated,
  effective,
  sections = [],
  sibling,
  children,
}: LegalLayoutProps): JSX.Element {
  const [activeId, setActiveId] = useState<string | undefined>(sections[0]?.id);

  // Scroll-spy — highlight the TOC entry whose section is at the top of the
  // viewport. Mirrors the screens implementation 1:1.
  useEffect(() => {
    if (sections.length === 0) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) {
          return;
        }
        const top = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b,
        );
        setActiveId(top.target.id);
      },
      { rootMargin: '-96px 0px -60% 0px', threshold: [0, 1] },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) {
        observer.observe(el);
      }
    });
    return () => observer.disconnect();
  }, [sections]);

  function handlePrint(): void {
    if (typeof window !== 'undefined' && typeof window.print === 'function') {
      window.print();
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div
              aria-hidden="true"
              className="flex h-8 w-8 items-center justify-center rounded-md bg-nipost-green text-sm font-bold text-white"
            >
              NP
            </div>
            <span className="hidden sm:inline text-xs uppercase tracking-[0.2em] text-gray-400 font-medium border-l border-gray-200 pl-3">
              Nigerian Postal Service
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {sibling ? (
              <Link
                to={sibling.to}
                className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-600 hover:text-nipost-green transition-colors"
              >
                {sibling.label} <ChevronRight className="w-4 h-4" />
              </Link>
            ) : null}
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-nipost-green border border-gray-200 hover:border-nipost-green rounded-lg px-3 py-1.5 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Print</span>
            </button>
            <Link
              to="/"
              className="text-sm font-medium text-gray-600 hover:text-nipost-green transition-colors"
            >
              ← Home
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-nipost-gold">Legal</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-gray-900">{title}</h1>
          {subtitle ? <p className="mt-3 text-gray-500 max-w-3xl">{subtitle}</p> : null}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-500">
            {effective ? (
              <span>
                <span className="font-semibold text-gray-700">Effective:</span> {effective}
              </span>
            ) : null}
            {lastUpdated ? (
              <span>
                <span className="font-semibold text-gray-700">Last updated:</span> {lastUpdated}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Body + TOC */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[260px_minmax(0,1fr)] gap-10">
        {/* TOC */}
        <aside className="hidden lg:block print:hidden">
          <div className="sticky top-20">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-3">
              On this page
            </p>
            <nav aria-label="On this page" className="space-y-1 border-l border-gray-200">
              {sections.map((s, i) => {
                const active = activeId === s.id;
                return (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className={`block pl-4 -ml-px py-1.5 text-sm transition-colors border-l-2 ${
                      active
                        ? 'border-nipost-green text-nipost-green font-semibold'
                        : 'border-transparent text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    <span className="text-[10px] font-mono text-gray-400 mr-2">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {s.label}
                  </a>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Document */}
        <article className="legal-prose bg-white border border-gray-100 rounded-2xl p-6 sm:p-10 print:p-0 print:border-0 print:bg-transparent">
          {children}
          <hr className="my-10 border-gray-100" />
          <div className="text-xs text-gray-500 space-y-1">
            <p className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" />
              Contact:{' '}
              <a className="text-nipost-green hover:underline" href="mailto:legal@nipost.gov.ng">
                legal@nipost.gov.ng
              </a>
              {' · '}
              <a className="text-nipost-green hover:underline" href="mailto:dpo@nipost.gov.ng">
                dpo@nipost.gov.ng
              </a>
            </p>
            <p className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5" />
              Customer support: 0800-NIPOST (0800-647-678) · 9:00–17:00 WAT, Monday–Saturday
            </p>
            <p>
              Postal address: NIPOST Headquarters, Garki Area 1, Abuja, Federal Capital Territory,
              Nigeria.
            </p>
          </div>
        </article>
      </div>

      {/* Inline prose styling — kept inline (matches the screens source) so
          the legal pages render correctly without needing a global stylesheet
          edit. */}
      <style>{`
        .legal-prose h2 {
          font-size: 1.5rem;
          font-weight: 800;
          color: #111827;
          margin-top: 2rem;
          padding-top: 1rem;
          scroll-margin-top: 5rem;
        }
        .legal-prose h2:first-child { margin-top: 0; padding-top: 0; }
        .legal-prose h3 { font-size: 1.05rem; font-weight: 700; color: #111827; margin-top: 1.25rem; margin-bottom: 0.5rem; }
        .legal-prose p { color: #374151; line-height: 1.7; margin: 0.75rem 0; font-size: 0.95rem; }
        .legal-prose ul, .legal-prose ol { margin: 0.75rem 0 0.75rem 1.25rem; color: #374151; line-height: 1.7; font-size: 0.95rem; }
        .legal-prose ul { list-style: disc; }
        .legal-prose ol { list-style: decimal; }
        .legal-prose li { margin: 0.25rem 0; }
        .legal-prose strong { color: #111827; font-weight: 600; }
        .legal-prose a { color: #27245f; }
        .legal-prose a:hover { text-decoration: underline; }
        .legal-prose code { background: #f3f4f6; padding: 0.05rem 0.4rem; border-radius: 0.25rem; font-size: 0.85em; }
        .legal-prose .callout {
          border-left: 4px solid #FDD700;
          background: #FFFBEB;
          padding: 0.875rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.9rem;
          color: #78350f;
          margin: 1rem 0;
        }
        .legal-prose .callout strong { color: #78350f; }
        .legal-prose table { width: 100%; border-collapse: collapse; margin: 0.75rem 0; font-size: 0.9rem; }
        .legal-prose th, .legal-prose td { border: 1px solid #e5e7eb; padding: 0.5rem 0.75rem; text-align: left; vertical-align: top; }
        .legal-prose th { background: #f9fafb; font-weight: 600; color: #111827; }
        @media print {
          body { background: white; }
          .legal-prose h2 { page-break-after: avoid; }
        }
      `}</style>
    </div>
  );
}
